import {
  applyLocationScope,
  ChartFilters,
  NO_MATCH_LOCATION_ID,
} from "hooks/useAdminGroup/applyLocationScope";
import type { ScopedLocationIds } from "hooks/useAdminGroup/useScopedLocationIds";
import type { HierarchyExternalIds } from "hooks/useAdminGroup/useHierarchyExternalIds";

/**
 * The subset of the breadcrumb scope the decision table reads. Deliberately a
 * Pick rather than the full ScopedLocationIds: useScopedApiFilters passes
 * exactly these three fields (they are its memo dependencies), so widening
 * this type would silently decouple the memoization from the inputs.
 */
export type ScopeFilterInputs = Pick<
  ScopedLocationIds,
  "isLbaEnabled" | "applyScope" | "scopeExternalIds"
>;

export interface DeriveScopedApiFiltersInput {
  apiFilters: ChartFilters;
  /** The current section's filter options (Redux controls state). */
  filterOptions?: unknown;
  scope: ScopeFilterInputs;
  hierarchy: HierarchyExternalIds;
}

export interface DeriveScopedApiFiltersResult {
  /** The filters to fetch with. */
  apiFilters: ChartFilters;
  /** Render a spinner instead: a needed input (the hierarchy) is loading. */
  hold: boolean;
  /**
   * Render the empty state instead; no request could return rows (the
   * scope ∩ manual-location-filter intersection is empty).
   */
  isEmptyIntersection: boolean;
}

// Pure decision table for the widgets that fetch OUTSIDE ReportPageChart --
// the Analytics tab's sections (ScopedSection) and the Quality tab's
// panels/tables/header strip. Derives the apiFilters every such widget
// fetches with from the section's controls state, the breadcrumb scope, and
// the hierarchy.
//
// The Location view seeds `apiFilters.locations` from the /nextos/locations
// options (Mongo OCI cache). That cache can legitimately be empty while the
// breadcrumb hierarchy (AGS/VSOS) still has locations -- the seed is then
// `locations: []`, which is NOT a user narrowing: the location filter UI is
// hidden entirely when there are no options (trend_controls renders it only
// when filterOptions.length). Left alone, that seed zeroes every chart
// (empty IN) or, intersected with a scope, sentinels straight to NoData.
// The degraded-seed rows below recover it from the breadcrumb instead:
//
//   options [] + node selected -> locations = scope subtree ids (+ the
//                                 scope_locations enforcement key)
//   options [] + root/default  -> locations = ALL hierarchy node ids (the
//                                 breadcrumb is the source of truth; no
//                                 scope_locations -- root means unnarrowed)
//   options [] + root, hierarchy loading -> hold
//   options [] + root, hierarchy failed  -> fail open like the rest of LBA
//                                 (breadcrumb renders disabled): drop the
//                                 empty key, account-wide
//   options [] + LBA off       -> untouched (pre-existing app behavior)
//
// A genuine user remove-all (options exist, selection emptied via the
// MultiSelect) keeps today's semantics: sentinel/NoData under a scope,
// passthrough (zero rows) without one.
export const deriveScopedApiFilters = ({
  apiFilters,
  filterOptions,
  scope,
  hierarchy,
}: DeriveScopedApiFiltersInput): DeriveScopedApiFiltersResult => {
  const locations = apiFilters?.locations;
  const hasLocationsKey = Array.isArray(locations);
  const optionsEmpty = !Array.isArray(filterOptions) || filterOptions.length === 0;
  const isDegradedSeed =
    hasLocationsKey && locations.length === 0 && optionsEmpty;

  if (isDegradedSeed && scope.isLbaEnabled && apiFilters) {
    if (scope.applyScope) {
      // Fail closed on a malformed scope set, same as applyLocationScope.
      const ids =
        scope.scopeExternalIds instanceof Set
          ? Array.from(scope.scopeExternalIds)
          : [];
      const scoped = ids.length > 0 ? ids : [NO_MATCH_LOCATION_ID];

      return {
        hold: false,
        isEmptyIntersection: false,
        apiFilters: { ...apiFilters, locations: scoped, scope_locations: scoped },
      };
    }

    // Root/default selection (scope kind NONE).
    if (!hierarchy.isFailed && !hierarchy.isReady) {
      return { hold: true, isEmptyIntersection: false, apiFilters };
    }
    if (hierarchy.isFailed || hierarchy.allExternalIds.length === 0) {
      const { locations: _dropped, ...rest } = apiFilters;
      return { hold: false, isEmptyIntersection: false, apiFilters: rest };
    }
    return {
      hold: false,
      isEmptyIntersection: false,
      apiFilters: { ...apiFilters, locations: hierarchy.allExternalIds },
    };
  }

  // Non-degraded paths: unchanged ScopedSection behavior. A manual location
  // filter intersects with the scope client-side (the location *bucket*
  // list must stay in-scope too); an empty intersection short-circuits.
  const intersected = hasLocationsKey
    ? applyLocationScope(apiFilters, scope.applyScope, scope.scopeExternalIds)
    : apiFilters;

  const isEmptyIntersection =
    scope.applyScope &&
    Array.isArray(intersected?.locations) &&
    intersected.locations.length === 1 &&
    intersected.locations[0] === NO_MATCH_LOCATION_ID;

  if (!scope.applyScope) {
    return { hold: false, isEmptyIntersection: false, apiFilters: intersected };
  }

  const ids =
    scope.scopeExternalIds instanceof Set
      ? Array.from(scope.scopeExternalIds)
      : [];

  return {
    hold: false,
    isEmptyIntersection,
    apiFilters: {
      ...(intersected ?? {}),
      scope_locations: ids.length > 0 ? ids : [NO_MATCH_LOCATION_ID],
    },
  };
};
